export const SELECTED_BILLING_INTERVAL_STORAGE_KEY = 'selectedBillingInterval';

/** Query param on finish-signup when Stripe Checkout cancel/back is used. */
export const FINISH_SIGNUP_CHECKOUT_CANCELLED_PARAM = 'checkout';
export const FINISH_SIGNUP_CHECKOUT_CANCELLED_VALUE = 'cancelled';

export type BillingInterval = 'month' | 'year';

export const DEFAULT_BILLING_INTERVAL: BillingInterval = 'year';

export function parseBillingInterval(
  value: string | null | undefined,
): BillingInterval | null {
  if (value === 'month' || value === 'year') {
    return value;
  }
  return null;
}

/** URL param → sessionStorage → default (year). */
export function resolveInitialBillingInterval(
  urlParam: string | null | undefined,
  storageValue: string | null | undefined,
  defaultInterval: BillingInterval = DEFAULT_BILLING_INTERVAL,
): BillingInterval {
  return (
    parseBillingInterval(urlParam) ??
    parseBillingInterval(storageValue) ??
    defaultInterval
  );
}

export function buildFinishSignupPath(options?: {
  planId?: string | null;
  billingInterval?: BillingInterval | null;
}): string {
  const params = new URLSearchParams();
  if (options?.planId) {
    params.set('planId', options.planId);
  }
  if (options?.billingInterval) {
    params.set('billingInterval', options.billingInterval);
  }
  const query = params.toString();
  return query ? `/finish-signup?${query}` : '/finish-signup';
}

export function readStoredBillingInterval(): BillingInterval | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return parseBillingInterval(sessionStorage.getItem(SELECTED_BILLING_INTERVAL_STORAGE_KEY));
}

export function persistBillingInterval(interval: BillingInterval): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(SELECTED_BILLING_INTERVAL_STORAGE_KEY, interval);
}

export function clearStoredBillingInterval(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(SELECTED_BILLING_INTERVAL_STORAGE_KEY);
}

export function buildFinishSignupPathFromSessionStorage(planId?: string | null): string {
  return buildFinishSignupPath({
    planId,
    billingInterval: readStoredBillingInterval(),
  });
}

/** Stripe Checkout cancel_url — plan selection without auto-redirect to checkout. */
export function buildFinishSignupCancelUrl(origin?: string): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const params = new URLSearchParams();
  params.set(FINISH_SIGNUP_CHECKOUT_CANCELLED_PARAM, FINISH_SIGNUP_CHECKOUT_CANCELLED_VALUE);
  return `${base}/finish-signup?${params.toString()}`;
}

export function isFinishSignupCheckoutCancelled(
  searchParams: URLSearchParams | { get(name: string): string | null },
): boolean {
  return searchParams.get(FINISH_SIGNUP_CHECKOUT_CANCELLED_PARAM) === FINISH_SIGNUP_CHECKOUT_CANCELLED_VALUE;
}

/** Clears stored plan preselection so finish-signup shows plan picker instead of auto-checkout. */
export function clearCheckoutPlanPreselection(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem('selectedAcademyPlanId');
  sessionStorage.removeItem('selectedClubPlanId');
  sessionStorage.removeItem('selectedPlanId');
}
